import React, { useRef } from 'react';
import clsx from 'clsx';
import { BaseActionButtonProps } from '../BaseActionButtonProps';
import commonStyles from '../ActionButtonCommon.module.scss';
import styles from './ShopButton.module.scss';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { BaseContentResponse } from 'lib/services/models/content-response';
import useDictionary from 'lib/hooks/useDictionary';
import { useContentItem } from 'lib/hooks/useContentItem';

export interface ShopButtonProps extends BaseActionButtonProps {
  disabled?: boolean;
  contentItem?: BaseContentResponse;
  isSubAction?: boolean;
}

export const ShopButton = ({
  label,
  variant,
  disabled,
  contentItem,
}: ShopButtonProps): JSX.Element => {
  const contentItemFallback = useContentItem();
  const content = contentItem ?? contentItemFallback;
  const contentId = content?.ContentId ?? '';
  const contentTitle = content?.Title ?? '';
  const contentType = content?.ContentType ?? '';
  const recordType = content?.RecordType ?? '';
  const { getDictionaryValue } = useDictionary();
  const labelText = getDictionaryValue('Components.ActionToolBar.Shop') || 'Shop';
  const buttonRef = useRef<HTMLButtonElement>(null);

  const gtmEvent = {
    content_id: contentId,
    content_title: contentTitle,
    content_type: contentType,
    record_type: recordType,
    event: 'buy_intent',
    position: 'Floating Toolbar',
    vendor_experience: 'Pear_product',
    content_list_name: 'RelatedContent',
    template_name: 'RelatedContent',
  };

  const scrollToPearWidget = () => {
    const pearWidget =
      document.getElementById('pear-map-target') || document.getElementById('pear-pdp-target');

    if (!pearWidget) {
      console.warn('Pear Commerce widget not found');
      return;
    }

    // Smooth scroll to the widget
    pearWidget.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

    // Apply highlight effect after scroll completes
    setTimeout(() => {
      // Add highlight
      pearWidget.classList.add('highlightBorder');

      // Move focus to widget for keyboard navigation
      pearWidget.focus();

      // Fade out after 2s
      setTimeout(() => {
        pearWidget.classList.add('highlightFadeOut');
      }, 2000);

      // Cleanup
      setTimeout(() => {
        pearWidget.classList.remove('highlightBorder', 'highlightFadeOut');
      }, 2600);
    }, 500);
  };

  const handleClick = () => {
    scrollToPearWidget();
  };

  return (
    <div
      className={clsx({
        [commonStyles['actionButtonWrapper--horizontal']]: variant === 'horizontal',
      })}
    >
      <CtaButton
        ref={buttonRef}
        ctaStyle="none"
        ctaVariant="brand"
        onClick={handleClick}
        disabled={disabled}
        aria-label={label ? label : labelText}
        className={clsx(
          commonStyles.actionButtonBase,
          commonStyles[`actionButton--${variant}`],
          styles.shopButton,
          styles[variant]
        )}
        ctaIconClassName={commonStyles.icon}
        ctaIconNoSpacer={variant === 'vertical'}
        gtmEvent={gtmEvent}
        ctaIconBefore="shop"
        ctaIconSize="sm"
        text={label ? label : labelText}
      />
    </div>
  );
};
